import { useState, useEffect } from 'react'
import { serverNow } from '../lib/clock'
import { MapPin, User, RefreshCw, Clock, Mountain } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useWS } from '../contexts/WSContext'
import { useScope } from '../contexts/ScopeContext'
import { ScopeChip } from './MineScopeSelect'
import { equipmentOf, statusOf, compareDevices } from '../equipment'
import { statusText, cycleStateLabel } from '../i18n/status'
import { fmtDurShort } from '../i18n/format'

// Tempo decorrido desde `since` até `until` (padrão: agora), formato curto.
function fmtDur(since, until) {
  if (!since) return '—'
  // O instante de referência vem do servidor: `since` foi carimbado lá, e subtrair
  // dele o relógio da estação começa o cronômetro já deslocado, ou preso em 0s.
  const end = until ?? serverNow()
  return fmtDurShort(Math.max(0, (end - new Date(since).getTime()) / 1000))
}

function Cell({ icon: Icon, label, children, className = '' }) {
  return (
    <div className={`min-w-0 ${className}`}>
      <div className="flex items-center gap-1 text-[11px] text-slate-400 dark:text-slate-500"><Icon size={11} /> {label}</div>
      <div className="text-sm text-slate-700 dark:text-slate-200 truncate mt-0.5">{children}</div>
    </div>
  )
}

function TrackingRow({ device }) {
  const { t } = useTranslation()
  const { categories, mines } = useWS()
  const eq = equipmentOf(device, categories, mines)
  const st = statusOf(device)
  // "Desligado" há ≥5s volta ao estado inicial (Aguardando, sem tempo) — o backend também
  // zera, esta guarda só garante o display imediato via ticker.
  const stale = device.cycleState === 'Desligado' && device.cycleSince &&
    (serverNow() - new Date(device.cycleSince)) >= 5000
  const cycle = stale ? null : device.cycleState
  const offline = st.key === 'down'
  const operator = device.operatorName

  return (
    <div className="flex items-center gap-4 bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-700 shadow-sm hover:shadow-md hover:border-slate-300 transition-all overflow-hidden pr-4">
      <span className={`self-stretch w-1.5 shrink-0 ${st.bar}`} />

      <div className="flex items-center gap-3 py-3 w-56 shrink-0">
        <div className="flex items-center justify-center w-12 h-12 rounded-lg bg-slate-100 dark:bg-slate-800 shrink-0">
          <span className="equip-icon block w-7 h-7 text-slate-500 dark:text-slate-400" style={{ WebkitMaskImage: `url(/equipments/${eq.svg}.svg)`, maskImage: `url(/equipments/${eq.svg}.svg)` }} />
        </div>
        <div className="min-w-0">
          <p className="font-semibold text-slate-800 dark:text-slate-100 text-sm leading-tight truncate">{eq.name || device.name}</p>
          <p className="text-xs text-slate-400 dark:text-slate-500 truncate">{eq.label}</p>
        </div>
      </div>

      <Cell icon={MapPin} label={t('ops:drawer.mine_pit')} className="w-44">
        {eq.mine ? (eq.pit ? `${eq.mine} · ${eq.pit}` : eq.mine) : <span className="text-slate-300 dark:text-slate-600">—</span>}
      </Cell>

      <Cell icon={User} label={t('common:entities.operator')} className="w-40">
        {operator ? operator : <span className="text-slate-300 dark:text-slate-600">{t('ops:tracking.no_operator')}</span>}
      </Cell>

      <Cell icon={RefreshCw} label={t('ops:tracking.cycle_state_label')} className="flex-1">
        {cycle ? (
          <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium ${st.soft} ${st.text}`}>
            <span className={`w-1.5 h-1.5 rounded-full ${st.dot}`} /> {cycleStateLabel(t, cycle)}
          </span>
        ) : <span className="text-slate-300 dark:text-slate-600">{t('common:device_status.waiting')}</span>}
      </Cell>

      {/* Sem comunicação, o cronômetro para no último contato: um contador subindo em
          linha vermelha sugeria atividade numa máquina que não fala há horas. */}
      <Cell icon={Clock} label={t('ops:tracking.time_in_step')} className="w-28 text-right">
        <span className={`tabular-nums ${offline ? 'text-slate-400 dark:text-slate-500 italic' : ''}`}
          title={offline ? t('ops:tracking.frozen_at_last_seen') : undefined}>
          {cycle ? fmtDur(device.cycleSince, offline ? new Date(device.lastSeen).getTime() : undefined) : '—'}
        </span>
      </Cell>

      <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium shrink-0 ${st.soft} ${st.text}`}>
        <span className="relative flex h-1.5 w-1.5">
          <span className={`absolute inline-flex h-full w-full rounded-full ${st.dot} opacity-75 ${st.pulse ? 'animate-ping' : ''}`} />
          <span className={`relative inline-flex rounded-full h-1.5 w-1.5 ${st.dot}`} />
        </span>
        {statusText(t, st)}
      </span>
    </div>
  )
}

export default function TrackingView() {
  const { t } = useTranslation()
  const { devices, categories, mines } = useWS()
  const { matchesScope } = useScope()

  // Recalcula tempo-no-passo e status ao vivo, mesmo sem mensagem nova.
  const [, setTick] = useState(0)
  useEffect(() => {
    const id = setInterval(() => setTick(t => t + 1), 1000)
    return () => clearInterval(id)
  }, [])

  // O recorte de minas vem do seletor global de escopo (Topbar).
  const rows = devices
    .filter(matchesScope)
    .sort(compareDevices(d => equipmentOf(d, categories, mines).name || d.name))
  const ativos = rows.filter(d => statusOf(d).key === 'op').length

  return (
    <div className="h-full flex flex-col">
      <div className="bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-700 px-6 py-3 flex items-center gap-4 flex-wrap">
        <ScopeChip />
        <p className="text-sm text-slate-500 dark:text-slate-400">{t('ops:devices.equipment_count', { count: rows.length })} · {t('ops:devices.in_operation', { count: ativos })}</p>
        <div className="ml-auto flex items-center gap-1.5 text-xs text-slate-400 dark:text-slate-500"><RefreshCw size={13} /> {t('ops:tracking.live_cycle')}</div>
      </div>

      <div className="flex-1 overflow-y-auto p-6 scrollbar-thin space-y-3">
        {rows.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-64 text-slate-400 dark:text-slate-500">
            <Mountain size={40} className="mb-3 text-slate-300 dark:text-slate-600" />
            <p className="text-sm">{t('ops:tracking.none_in_scope')}</p>
          </div>
        ) : (
          rows.map(d => <TrackingRow key={d.name} device={d} />)
        )}
      </div>
    </div>
  )
}
